import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import {
  computeGranularity,
  toMetricGranularity,
  type DateRangeInput,
  type IngestionFilter,
} from '@/lib/api/system/ingestion-observability';
import {
  FetchMetricQuery,
  MetricCube,
  IngestionMeasure,
  IngestionBreakdown,
} from '@/lib/api/reporting/metrics';
import { useIngestionManifest } from '../hooks/useIngestionManifest';

interface IngestionFilteringBreakdownProps {
  tenantId: string;
  dateRange: DateRangeInput;
  filters?: IngestionFilter;
}

const STAGE_PALETTE = [
  '#f59e0b', '#f97316', '#eab308', '#d97706', '#ca8a04',
  '#b45309', '#a16207', '#8b5cf6', '#7c3aed', '#ec4899',
  '#14b8a6', '#06b6d4', '#84cc16', '#78716c', '#6366f1',
];

function getStageColor(_stage: string, index: number): string {
  return STAGE_PALETTE[index % STAGE_PALETTE.length];
}

export function IngestionFilteringBreakdown({ tenantId, dateRange, filters }: IngestionFilteringBreakdownProps) {
  const { stageDisplayNames } = useIngestionManifest();
  const granularity = computeGranularity(dateRange);
  const dateStart = new Date(dateRange.dateStart);
  const dateEnd = new Date(dateRange.dateEnd);

  // Event count by stage (no granularity) — pipeline funnel
  const { data: totalsData, isLoading: totalsLoading, error } = useQuery(
    FetchMetricQuery({
      tenantId,
      cube: MetricCube.IngestionEvents,
      measure: IngestionMeasure.EventCount,
      breakdown: IngestionBreakdown.Stage,
      dateStart,
      dateEnd,
      siteId: filters?.projectId,
      cameraId: filters?.videoProcessorConfigId,
    })
  );

  // Event count by stage over time
  const { data: timeData, isLoading: timeLoading } = useQuery(
    FetchMetricQuery({
      tenantId,
      cube: MetricCube.IngestionEvents,
      measure: IngestionMeasure.EventCount,
      breakdown: IngestionBreakdown.Stage,
      granularity: toMetricGranularity(granularity),
      dateStart,
      dateEnd,
      siteId: filters?.projectId,
      cameraId: filters?.videoProcessorConfigId,
    })
  );

  const isLoading = totalsLoading || timeLoading;

  const tickFormat = granularity === 'HOUR'
    ? { month: 'short' as const, day: 'numeric' as const, hour: '2-digit' as const, minute: '2-digit' as const }
    : { month: 'short' as const, day: 'numeric' as const };

  const { stages, stageTotals } = useMemo(() => {
    if (!totalsData?.data?.length) return { stages: [], stageTotals: [] };

    const byStage: Record<string, number> = {};
    for (const point of totalsData.data) {
      const stage = point.attributes?.['stage'] ?? '';
      byStage[stage] = (byStage[stage] ?? 0) + (point.value ?? 0);
    }

    const stages = Object.entries(byStage)
      .sort(([, a], [, b]) => b - a)
      .map(([stage]) => stage);

    const maxEvents = Math.max(...Object.values(byStage));
    const stageTotals = stages.map((stage) => ({
      stage,
      total: byStage[stage] ?? 0,
      pct: maxEvents > 0 ? ((byStage[stage] ?? 0) / maxEvents) * 100 : 0,
    }));

    return { stages, stageTotals };
  }, [totalsData]);

  const chartData = useMemo(() => {
    if (!timeData?.data?.length || stages.length === 0) return [];

    const byBucket: Record<string, Record<string, number>> = {};
    for (const point of timeData.data) {
      const stage = point.attributes?.['stage'] ?? '';
      const bucket = point.key ?? '';
      if (!byBucket[bucket]) byBucket[bucket] = {};
      byBucket[bucket][stage] = (byBucket[bucket][stage] ?? 0) + (point.value ?? 0);
    }

    return Object.entries(byBucket)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([bucket, counts]) => ({
        bucket: new Date(bucket).toLocaleString(undefined, tickFormat),
        ...counts,
      }));
  }, [timeData, stages, tickFormat]);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-[#2E3835] bg-[#1a1a1a] p-3">
        <Skeleton className="h-[220px] w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-400">
        Failed to load stage breakdown: {error.message}
      </div>
    );
  }

  if (stages.length === 0) return null;

  return (
    <div className="rounded-lg border border-[#2E3835] bg-[#1a1a1a]">
      <div className="px-3 pt-3 pb-1">
        <h3 className="text-sm font-medium text-white">Pipeline Stages</h3>
        <p className="text-[10px] text-white/40">
          Event volume per pipeline stage — shows where events flow
        </p>
      </div>

      {/* Stage volume bars */}
      <div className="px-3 pb-3">
        <div className="space-y-1.5">
          {stageTotals.map((s, i) => {
            const color = getStageColor(s.stage, i);
            return (
              <div key={s.stage} className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1.5 w-[200px] shrink-0">
                  <span
                    className="inline-block h-2 w-2 rounded-sm shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span className="truncate text-white/60" title={stageDisplayNames.get(s.stage) ?? s.stage}>
                    {stageDisplayNames.get(s.stage) ?? s.stage}
                  </span>
                </div>
                <div className="flex-1 flex h-3 overflow-hidden rounded-full bg-white/5">
                  <div
                    style={{ width: `${s.pct}%`, backgroundColor: color, opacity: 0.7 }}
                  />
                </div>
                <span className="w-[80px] shrink-0 text-right tabular-nums text-white/40">
                  {s.total.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Over-time chart */}
      {chartData.length > 0 && (
        <div className="border-t border-[#2E3835] px-3 py-3">
          <p className="mb-2 text-[10px] text-white/40">
            {granularity === 'HOUR' ? 'Hourly' : granularity === 'DAY' ? 'Daily' : 'Weekly'} volume by stage
          </p>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                <defs>
                  {stages.map((stage, i) => {
                    const color = getStageColor(stage, i);
                    return (
                      <linearGradient key={stage} id={`flt-${stage}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                      </linearGradient>
                    );
                  })}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2E3835" vertical={false} />
                <XAxis
                  dataKey="bucket"
                  tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 9 }}
                  interval="preserveStartEnd"
                  axisLine={{ stroke: '#2E3835' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  content={({ active, label, payload }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="rounded-lg border border-[#2E3835] bg-black/95 px-3 py-2 text-xs backdrop-blur-sm">
                        <div className="mb-1.5 font-medium text-white/70">{label}</div>
                        {stages.map((stage, i) => {
                          const entry = payload.find((p) => p.dataKey === stage);
                          const val = (entry?.value as number) ?? 0;
                          if (val === 0) return null;
                          return (
                            <div key={stage} className="flex items-center gap-1.5 py-0.5">
                              <span
                                className="inline-block h-2 w-2 rounded-sm shrink-0"
                                style={{ backgroundColor: getStageColor(stage, i) }}
                              />
                              <span className="text-white/70">{stageDisplayNames.get(stage) ?? stage}</span>
                              <span className="ml-auto pl-4 tabular-nums text-white/50">
                                {val.toLocaleString()}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }}
                  cursor={{ stroke: 'rgba(255,255,255,0.1)' }}
                />
                {stages.map((stage, i) => (
                  <Area
                    key={stage}
                    type="monotone"
                    dataKey={stage}
                    stroke={getStageColor(stage, i)}
                    strokeWidth={1.5}
                    fill={`url(#flt-${stage})`}
                    dot={false}
                    stackId="stages"
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
